/**
 * HOH Theme — Intersection Observer Performance Utility
 * ======================================================
 * Provides:
 *  1. Lazy image loading  (data-src / data-srcset attributes)
 *  2. Reveal-on-scroll animations  (data-hoh-reveal)
 *  3. Video pause/play based on viewport visibility (data-hoh-video)
 *  4. Deferred section hydration (data-hoh-defer)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     1. LAZY IMAGE / BACKGROUND LOADER
     Swap data-src → src and data-srcset → srcset when image enters viewport.
     Also supports background images via data-bg.
  ───────────────────────────────────────────────────────────── */
  function initLazyImages() {
    const lazyImages = document.querySelectorAll(
      'img[data-src], img[data-srcset], [data-bg]'
    );
    if (!lazyImages.length) return;

    const imgObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          if (el.tagName === 'IMG') {
            if (el.dataset.src) {
              el.src = el.dataset.src;
              delete el.dataset.src;
            }
            if (el.dataset.srcset) {
              el.srcset = el.dataset.srcset;
              delete el.dataset.srcset;
            }
            el.classList.add('hoh-lazy-loaded');
          } else if (el.dataset.bg) {
            el.style.backgroundImage = 'url(' + el.dataset.bg + ')';
            delete el.dataset.bg;
            el.classList.add('hoh-lazy-loaded');
          }

          observer.unobserve(el);
        });
      },
      {
        rootMargin: '200px 0px 200px 0px', // Start loading before element is visible
        threshold: 0,
      }
    );

    lazyImages.forEach(function (img) {
      imgObserver.observe(img);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     2. SCROLL-REVEAL ANIMATIONS
     Add data-hoh-reveal to any element for a fade-up-in reveal
     on first entry. Supports data-hoh-delay (ms) for stagger.
  ───────────────────────────────────────────────────────────── */
  function initRevealAnimations() {
    const revealEls = document.querySelectorAll('[data-hoh-reveal]');
    if (!revealEls.length) return;

    const revealObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = el.dataset.hohDelay || 0;

          setTimeout(function () {
            el.classList.add('hoh-revealed');
          }, parseInt(delay, 10));

          observer.unobserve(el);
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -60px 0px',
      }
    );

    revealEls.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     3. VIDEO VIEWPORT MANAGER
     Automatically pause <video> elements when they leave the
     viewport and resume when they return.
     Target: add data-hoh-video to a <video> or its wrapper.
  ───────────────────────────────────────────────────────────── */
  function initVideoViewport() {
    // Find all videos that should auto-pause when off-screen
    var videoContainers = document.querySelectorAll('[data-hoh-video]');
    if (!videoContainers.length) return;

    var videoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var container = entry.target;
          var videos = container.tagName === 'VIDEO'
            ? [container]
            : container.querySelectorAll('video');

          videos.forEach(function (video) {
            if (entry.isIntersecting) {
              // Only play if it was playing before (has autoplay or was running)
              if (video.paused && video.dataset.hohWasPlaying === 'true') {
                var promise = video.play();
                if (promise !== undefined) promise.catch(function () {});
              }
            } else {
              if (!video.paused) {
                video.dataset.hohWasPlaying = 'true';
                video.pause();
              }
            }
          });
        });
      },
      {
        threshold: 0.25, // Pause when less than 25% is visible
      }
    );

    videoContainers.forEach(function (container) {
      // Mark all child videos as playing by default (they have autoplay)
      var videos = container.tagName === 'VIDEO'
        ? [container]
        : container.querySelectorAll('video');
      videos.forEach(function (v) {
        v.dataset.hohWasPlaying = 'true';
      });
      videoObserver.observe(container);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     4. SECTION HYDRATION DEFERRAL
     Elements with data-hoh-defer will be made visible only once
     they scroll into view. Use with display:none or visibility:hidden
     sections that have heavy JS, so the browser does not parse their
     scripts until needed.
  ───────────────────────────────────────────────────────────── */
  function initDeferredSections() {
    var deferredSections = document.querySelectorAll('[data-hoh-defer]');
    if (!deferredSections.length) return;

    var sectionObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var section = entry.target;

          // Trigger custom event so section-specific JS can initialize
          section.dispatchEvent(new CustomEvent('hoh:section-visible', { bubbles: true }));
          section.classList.add('hoh-section-visible');
          section.removeAttribute('data-hoh-defer');

          observer.unobserve(section);
        });
      },
      {
        rootMargin: '400px 0px 400px 0px', // Hydrate well before it's visible
        threshold: 0,
      }
    );

    deferredSections.forEach(function (section) {
      sectionObserver.observe(section);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     5. GLOBAL CSS INJECTION  (for reveal animations)
  ───────────────────────────────────────────────────────────── */
  function injectRevealStyles() {
    var styleId = 'hoh-observer-styles';
    if (document.getElementById(styleId)) return;

    var style = document.createElement('style');
    style.id = styleId;
    style.textContent = [
      '[data-hoh-reveal] {',
      '  opacity: 0;',
      '  transform: translateY(24px);',
      '  transition: opacity 0.7s cubic-bezier(0.23, 1, 0.32, 1),',
      '              transform 0.7s cubic-bezier(0.23, 1, 0.32, 1);',
      '  will-change: opacity, transform;',
      '}',
      '[data-hoh-reveal].hoh-revealed {',
      '  opacity: 1;',
      '  transform: translateY(0);',
      '}',
      'img[data-src], img[data-srcset] {',
      '  transition: opacity 0.4s ease;',
      '}',
      'img.hoh-lazy-loaded {',
      '  opacity: 1;',
      '}',
    ].join('\n');

    document.head.appendChild(style);
  }

  /* ─────────────────────────────────────────────────────────────
     BOOT — Run all observers
  ───────────────────────────────────────────────────────────── */
  function boot() {
    injectRevealStyles();
    initLazyImages();
    initRevealAnimations();
    initVideoViewport();
    initDeferredSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run when Shopify theme editor adds new sections
  document.addEventListener('shopify:section:load', function () {
    boot();
  });

})();
